import { type ComputedRef, computed } from 'vue'
import { roomIdToChatId } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'

export function canPinChatInList(
  chatId: number | string | null | undefined,
  matrixRoomId?: string | null,
): boolean {
  if (chatId == null || chatId === '') {
    return false
  }

  const id = Number(chatId)
  if (!Number.isFinite(id)) {
    return false
  }

  // Только явный synthetic: id = hash(roomId). Нельзя звать isSyntheticMatrixChatId —
  // там коллизии hash с маленькими backend id.
  return !(matrixRoomId && id === roomIdToChatId(matrixRoomId))
}

export function buildPinChatMenuItem(options: {
  isPinned: boolean
  onClick: () => void
}) {
  return {
    title: options.isPinned ? 'Открепить' : 'Закрепить',
    value: 'Pin Chat',
    prependIcon: options.isPinned ? 'ri-unpin-line' : 'ri-pushpin-2-line',
    onClick: options.onClick,
  }
}

/** Контекст-меню закрепить/открепить для строки списка. */
export function useChatPinMenu(options: {
  chatId: () => number | string | null | undefined
  matrixRoomId?: () => string | null | undefined
  isPinned: () => boolean
  enabled?: () => boolean
}): {
    canPinThisChat: ComputedRef<boolean>
    isChatPinned: ComputedRef<boolean>
    togglePinChat: () => void
    pinMenuItem: ComputedRef<ReturnType<typeof buildPinChatMenuItem>>
  } {
  const chatStoreCore = useChatStoreCore()

  const canPinThisChat = computed(() => {
    if (options.enabled && !options.enabled()) {
      return false
    }

    return canPinChatInList(options.chatId(), options.matrixRoomId?.())
  })

  const isChatPinned = computed(() => Boolean(options.isPinned()))

  const togglePinChat = () => {
    const rawId = options.chatId()
    if (rawId == null || rawId === '') {
      return
    }

    const id = Number(rawId)
    if (!Number.isFinite(id)) {
      return
    }

    void chatStoreCore.setChatPinned(id, !isChatPinned.value)
  }

  const pinMenuItem = computed(() =>
    buildPinChatMenuItem({
      isPinned: isChatPinned.value,
      onClick: togglePinChat,
    }),
  )

  return {
    canPinThisChat,
    isChatPinned,
    togglePinChat,
    pinMenuItem,
  }
}
